// Owner refund (POST /api/payments/:id/refund { amount, reason }). Partial refunds
// are allowed up to the refundable remainder. The amount starts at that
// remainder, so confirming it unchanged is the full-amount void when LawPay
// can still void the charge. The body stays { amount, reason }. LawPay refunds
// report pending / completed / failed; a hand-recorded payment is not sent to LawPay.
// Delivered 2026-11-01.
import React, { useState } from 'react';
import { adminApi } from '../utils/adminApi';
import { listRefunds } from '../utils/paymentRefundDisplay';

const F = { fontFamily: 'Arial, sans-serif' };

export function money(n) {
  const v = Number.isFinite(+n) ? +n : 0;
  const sign = v < 0 ? '-' : '';
  return sign + '$' + Math.abs(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Backend field is exactly refundable_remaining (POST refund is owner-only;
// partial refunds are allowed up to this amount).
export function refundableRemainder(payment) {
  if (!payment || payment.refundable_remaining == null || payment.refundable_remaining === '') return 0;
  const n = parseFloat(payment.refundable_remaining);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, +n.toFixed(2));
}

export function paymentRefunds(payment) {
  return listRefunds(payment);
}

export function paymentCanBeRefunded(payment) {
  if (!payment) return false;
  const status = String(payment.status || '').toLowerCase();
  if (payment.is_reversed || status === 'reversed' || status === 'voided') return false;
  return refundableRemainder(payment) > 0.009;
}

const REFUND_BADGES = {
  pending:    { bg: '#fff3e0', fg: '#9a4b00', label: 'Pending' },
  processing: { bg: '#fff3e0', fg: '#9a4b00', label: 'Pending' },
  completed:  { bg: '#e4f3e8', fg: '#1e6b35', label: 'Completed' },
  succeeded:  { bg: '#e4f3e8', fg: '#1e6b35', label: 'Completed' },
  failed:     { bg: '#fdecea', fg: '#8a1c1c', label: 'Failed' },
  declined:   { bg: '#fdecea', fg: '#8a1c1c', label: 'Failed' },
  cleared:    { bg: '#e4f3e8', fg: '#1e6b35', label: 'Completed' },
};

export function RefundStatusBadge({ status }) {
  const key = String(status || '').toLowerCase();
  const s = REFUND_BADGES[key];
  if (!s) {
    if (!key || key === 'voided') return <span style={{ color: '#ccc' }}>—</span>;
    return <span style={{ ...F, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, padding: '2px 7px', borderRadius: 9, background: '#eee', color: '#555', whiteSpace: 'nowrap' }}>{key}</span>;
  }
  return <span style={{ ...F, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, padding: '2px 7px', borderRadius: 9, background: s.bg, color: s.fg, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

export function plainRefundError(err) {
  const code = String((err && err.code) || '');
  const status = err && err.status;
  if (status === 403 || /OWNER|FORBIDDEN|NOT_OWNER/i.test(code)) {
    return 'Only the firm owner can refund a payment.';
  }
  if (/EXCEED|TOO_LARGE|REFUNDABLE/i.test(code)) {
    return 'That amount is more than what is still available to refund.';
  }
  if (/REASON/i.test(code)) return 'A reason is required.';
  if (code === 'LAWPAY_DECLINED') {
    const server = err && typeof err.serverError === 'string' ? err.serverError.trim() : '';
    const message = err && err.message ? String(err.message).trim() : '';
    return server || message;
  }
  const msg = err && err.message ? String(err.message).trim() : '';
  if (!msg || msg === 'Request failed' || /sql|syntax|stack|token|ECONN/i.test(msg)) {
    return 'The refund could not be recorded. Check the amount and try again.';
  }
  return msg;
}

function isManual(payment) {
  return !payment || payment.source !== 'lawpay';
}

export default function RefundPaymentModal({ payment, onClose, onRefunded }) {
  const cap = refundableRemainder(payment);
  const [amount, setAmount] = useState(cap > 0 ? cap.toFixed(2) : '');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const manual = isManual(payment);

  function validate() {
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) return 'Enter a refund amount greater than zero.';
    if (amt - cap > 0.009) return `That amount is more than the ${money(cap)} still available to refund.`;
    if (!reason.trim()) return 'A reason is required.';
    return '';
  }

  function review() {
    const problem = validate();
    if (problem) { setErr(problem); return; }
    setErr('');
    setConfirming(true);
  }

  async function submit() {
    const problem = validate();
    if (problem) { setErr(problem); setConfirming(false); return; }
    const amt = +parseFloat(amount).toFixed(2);
    setBusy(true);
    setErr('');
    try {
      const result = await adminApi.refundPayment(payment.id, { amount: amt, reason: reason.trim() });
      const refund = result && (result.refund || result.payment_refund || result);
      const st = String((refund && refund.status) || '').toLowerCase();
      let text;
      if (st === 'pending' || st === 'processing') text = `Refund of ${money(amt)} submitted. It is pending.`;
      else if (st === 'failed' || st === 'declined') text = `Refund of ${money(amt)} was not completed.`;
      else if (manual) text = `Manual refund of ${money(amt)} recorded. It was not sent to LawPay.`;
      else text = `Refund of ${money(amt)} recorded.`;
      onRefunded(text);
    } catch (e) {
      setErr(plainRefundError(e));
      setBusy(false);
    }
  }

  const shell = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: '40px 20px', overflow: 'auto',
  };
  const card = {
    background: '#fff', borderRadius: 6, width: '100%', maxWidth: 480, padding: 24,
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)', ...F,
  };

  return (
    <div style={shell} onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div style={card}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 4px' }}>Refund payment</h2>
        <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
          {money(payment.amount)} payment{payment.client_name ? ` for ${payment.client_name}` : ''}. Up to <strong>{money(cap)}</strong> can still be refunded. A partial amount is fine.
        </p>
        {manual && (
          <p style={{ fontSize: 13, color: '#9a4b00', background: '#fff8e1', border: '1px solid #f0e0a0', borderRadius: 4, padding: '8px 10px', margin: '0 0 12px' }}>
            Manual refund — not sent to LawPay
          </p>
        )}
        {err && <div className="alert alert-error">{err}</div>}
        {!confirming ? (
          <>
            <div className="form-group">
              <label>Amount</label>
              <input type="number" min="0.01" step="0.01" max={cap || undefined} value={amount}
                onWheel={(e) => e.currentTarget.blur()}
                onChange={(e) => { setAmount(e.target.value); setErr(''); }}
                disabled={busy || cap <= 0} />
            </div>
            <div className="form-group">
              <label>Reason</label>
              <input type="text" value={reason}
                onChange={(e) => { setReason(e.target.value); setErr(''); }}
                placeholder="Why this payment is being refunded"
                disabled={busy} />
            </div>
            <div className="btn-row" style={{ margin: 0 }}>
              <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={review} disabled={busy || cap <= 0}>Continue</button>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: 14, margin: '0 0 16px' }}>
              Refund <strong>{money(parseFloat(amount))}</strong>?
              {manual
                ? ' This records the refund on the books only.'
                : ' LawPay will be asked to return this amount.'}
            </p>
            <p style={{ fontSize: 13, color: '#555', margin: '0 0 16px' }}><strong>Reason:</strong> {reason.trim()}</p>
            <div className="btn-row" style={{ margin: 0 }}>
              <button type="button" className="btn" onClick={() => { setConfirming(false); setErr(''); }} disabled={busy}>Back</button>
              <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>
                {busy ? 'Refunding…' : 'Confirm refund'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
